import { format, isValid, parse } from "date-fns";

/** Reference date for time-only parse/format (calendar day is ignored). */
const SCHEDULE_TIME_REFERENCE_DATE = new Date(2000, 0, 1);

const TWELVE_HOUR_PATTERN = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;
const TWENTY_FOUR_HOUR_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Converts combobox schedule time (e.g. `8:00 AM`) to GraphQL `Time` (`HH:mm`).
 */
export const to24HourTime = (time12h: string): string => {
  const trimmed = time12h.trim();
  const match = trimmed.match(TWELVE_HOUR_PATTERN);
  if (!match) {
    throw new RangeError("Invalid time format, expected 'hh:mm AM/PM'");
  }

  const hour12 = Number.parseInt(match[1] ?? "0", 10);
  const minutes = Number.parseInt(match[2] ?? "0", 10);

  if (hour12 < 1 || hour12 > 12) {
    throw new RangeError("Invalid time, hours must be between 00 and 23");
  }
  if (minutes < 0 || minutes > 59) {
    throw new RangeError("Invalid time, minutes must be between 00 and 59");
  }

  const parsed = parse(trimmed, "h:mm a", SCHEDULE_TIME_REFERENCE_DATE);
  if (!isValid(parsed)) {
    throw new RangeError("Invalid time format, expected 'hh:mm AM/PM'");
  }

  return format(parsed, "HH:mm");
};

/**
 * Converts GraphQL `Time` (`HH:mm` or `HH:mm:ss`) to combobox schedule time (e.g. `8:00 AM`).
 */
export const to12HourTime = (time24h: string): string => {
  const trimmed = time24h.trim();
  const match = trimmed.match(TWENTY_FOUR_HOUR_PATTERN);
  if (!match) {
    throw new RangeError("Invalid time format, expected 'HH:mm:ss' or 'HH:mm'");
  }

  const hours = Number.parseInt(match[1] ?? "0", 10);
  const minutes = Number.parseInt(match[2] ?? "0", 10);

  if (hours < 0 || hours > 23) {
    throw new RangeError("Invalid time, hours must be between 00 and 23");
  }
  if (minutes < 0 || minutes > 59) {
    throw new RangeError("Invalid time, minutes must be between 00 and 59");
  }

  const parsePattern = match[3] !== undefined ? "HH:mm:ss" : "HH:mm";
  const parsed = parse(trimmed, parsePattern, SCHEDULE_TIME_REFERENCE_DATE);
  if (!isValid(parsed)) {
    throw new RangeError("Invalid time format, expected 'HH:mm:ss' or 'HH:mm'");
  }

  return format(parsed, "h:mm a");
};
